export interface CropConfig {
  id: string;
  label: string;
  color: string;
}

export interface DemoFarmInputs {
  farmHectares: number;
  cropPercentages: Record<string, number>;
  seedRatesKgPerHa: Record<string, number>;
  seedCostsLocalPerKg: Record<string, number>;
}

export interface TestPacketsInputs {
  refugeeHouseholds: number;
  hostCommunityHouseholds: number;
  avgPacketsPerRefugeeHousehold: number;
  avgPacketsPerHostHousehold: number;
  packetWeightsKg: Record<string, number>;
}

export interface DemoFarmResults {
  seedRequirementsKg: Record<string, number>;
  hectaresPerCrop: Record<string, number>;
  costsLocal: Record<string, number>;
  costsUsd: Record<string, number>;
  totalSeedKg: number;
  totalCostLocal: number;
  totalCostUsd: number;
}

export interface TestPacketsResults {
  seedRequirementsKg: Record<string, number>;
  equivalentHectares: Record<string, number>;
  costsLocal: Record<string, number>;
  costsUsd: Record<string, number>;
  totalTestPackets: number;
  totalSeedKg: number;
  totalCostLocal: number;
  totalCostUsd: number;
}

export interface CalculationResults {
  demoFarm: DemoFarmResults;
  testPackets: TestPacketsResults;
  combined: {
    totalSeedKg: number;
    totalCostLocal: number;
    totalCostUsd: number;
  };
}

export const CROP_COLOR_PALETTE = [
  '#EAB308', // yellow
  '#DC2626', // red
  '#EA580C', // orange
  '#7C3AED', // purple
  '#059669', // emerald
  '#2563EB', // blue
  '#DB2777', // pink
  '#0891B2', // cyan
  '#84CC16', // lime
  '#6366F1', // indigo
  '#F59E0B', // amber
  '#14B8A6', // teal
];

// Light background colors derived from the palette above
const CROP_BG_PALETTE = [
  '#FEFCE8', // yellow-50
  '#FEF2F2', // red-50
  '#FFF7ED', // orange-50
  '#FAF5FF', // purple-50
  '#ECFDF5', // emerald-50
  '#EFF6FF', // blue-50
  '#FDF2F8', // pink-50
  '#ECFEFF', // cyan-50
  '#F7FEE7', // lime-50
  '#EEF2FF', // indigo-50
  '#FFFBEB', // amber-50
  '#F0FDFA', // teal-50
];

export function getCropBgColor(color: string): string {
  const idx = CROP_COLOR_PALETTE.indexOf(color);
  if (idx >= 0) return CROP_BG_PALETTE[idx];
  return '#F9FAFB'; // gray-50 fallback
}

export const DEFAULT_CROPS: CropConfig[] = [
  { id: 'maize', label: 'Maize', color: '#EAB308' },
  { id: 'bean', label: 'Bean', color: '#DC2626' },
  { id: 'cowpea', label: 'Cowpea', color: '#EA580C' },
  { id: 'eggplant', label: 'Eggplant', color: '#7C3AED' },
];

export interface CurrencyInfo {
  code: string;
  name: string;
  region: string;
  fallbackRate: number;
}

export const SUPPORTED_CURRENCIES: CurrencyInfo[] = [
  // Global
  { code: 'USD', name: 'US Dollar', region: 'Global', fallbackRate: 1 },
  // East Africa
  { code: 'UGX', name: 'Ugandan Shilling', region: 'East Africa', fallbackRate: 3500 },
  { code: 'KES', name: 'Kenyan Shilling', region: 'East Africa', fallbackRate: 130 },
  { code: 'TZS', name: 'Tanzanian Shilling', region: 'East Africa', fallbackRate: 2500 },
  { code: 'RWF', name: 'Rwandan Franc', region: 'East Africa', fallbackRate: 1300 },
  { code: 'ETB', name: 'Ethiopian Birr', region: 'East Africa', fallbackRate: 57 },
  { code: 'BIF', name: 'Burundian Franc', region: 'East Africa', fallbackRate: 2850 },
  { code: 'SSP', name: 'South Sudanese Pound', region: 'East Africa', fallbackRate: 950 },
  // West Africa
  { code: 'NGN', name: 'Nigerian Naira', region: 'West Africa', fallbackRate: 1550 },
  { code: 'GHS', name: 'Ghanaian Cedi', region: 'West Africa', fallbackRate: 15 },
  { code: 'XOF', name: 'CFA Franc (West)', region: 'West Africa', fallbackRate: 610 },
  // Central Africa
  { code: 'XAF', name: 'CFA Franc (Central)', region: 'Central Africa', fallbackRate: 610 },
  { code: 'CDF', name: 'Congolese Franc', region: 'Central Africa', fallbackRate: 2750 },
  // Southern Africa
  { code: 'ZAR', name: 'South African Rand', region: 'Southern Africa', fallbackRate: 18 },
  { code: 'MWK', name: 'Malawian Kwacha', region: 'Southern Africa', fallbackRate: 1700 },
  { code: 'ZMW', name: 'Zambian Kwacha', region: 'Southern Africa', fallbackRate: 27 },
  { code: 'MZN', name: 'Mozambican Metical', region: 'Southern Africa', fallbackRate: 64 },
];

export const DEFAULT_CURRENCY_CODE = 'UGX';

export type AreaUnit = 'ha' | 'acres';
export type WeightUnit = 'kg' | 'lbs' | 'g';

export const AREA_FACTORS: Record<AreaUnit, number> = { ha: 1, acres: 2.47105 };
export const WEIGHT_FACTORS: Record<WeightUnit, number> = { kg: 1, lbs: 2.20462, g: 1000 };

export const AREA_LABELS: Record<AreaUnit, string> = { ha: 'ha', acres: 'acres' };
export const WEIGHT_LABELS: Record<WeightUnit, string> = { kg: 'Kg', lbs: 'lbs', g: 'g' };

const IMPERIAL_COUNTRIES = new Set(['US', 'LR', 'GB', 'MM']);

export function getDefaultUnits(countryCode: string): { area: AreaUnit; weight: WeightUnit } {
  if (IMPERIAL_COUNTRIES.has(countryCode)) {
    return { area: 'acres', weight: 'lbs' };
  }
  return { area: 'ha', weight: 'kg' };
}
